import type { KeyboardEvent, PointerEvent } from 'react';
import { Box } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

export interface CanvasDragHandleProps {
  label: string;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
  /** dnd-kit's pointer listener only - the keyboard path is the arrow keys below, not dnd-kit's. */
  onPointerDown?: (event: PointerEvent<HTMLElement>) => void;
  onMove: (delta: -1 | 1) => void;
  testId: string;
}

/**
 * The grip in a canvas tab ("⠿ Heading", "⠿ Section"). Pointer: drag it anywhere on the page.
 * Keyboard: it is a focusable button and ArrowUp / ArrowDown move the element (or section) one
 * position, the same edit as the Sections panel's move arrows.
 */
export function CanvasDragHandle({ label, setActivatorNodeRef, onPointerDown, onMove, testId }: CanvasDragHandleProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    event.stopPropagation();
    onMove(event.key === 'ArrowUp' ? -1 : 1);
  };

  return (
    <Box
      component="button"
      type="button"
      ref={setActivatorNodeRef}
      aria-label={label}
      aria-roledescription="drag handle"
      aria-keyshortcuts="ArrowUp ArrowDown"
      onPointerDown={onPointerDown}
      onKeyDown={handleKeyDown}
      onClick={(event) => event.stopPropagation()}
      data-testid={testId}
      sx={{
        display: 'inline-flex',
        p: 0,
        border: 0,
        bgcolor: 'transparent',
        color: 'inherit',
        cursor: 'grab',
        touchAction: 'none',
        '&:focus-visible': { outline: 'var(--border-width-sm) solid var(--border-focus-on-color)', outlineOffset: 1 }
      }}
    >
      <DragIndicatorIcon sx={{ fontSize: 12 }} aria-hidden />
    </Box>
  );
}
